import {
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react';

interface ScrollExpandHeroProps {
  innerImageSrc: string;
  bgImageSrc: string;
  innerImageAlt?: string;
  bgImageAlt?: string;
  /** Slides LEFT on scroll (e.g. h1, reseller logos) */
  slideLeftContent?: ReactNode;
  /** Slides RIGHT on scroll (e.g. description, CTAs) */
  slideRightContent?: ReactNode;
  /** Stays static (e.g. language toggle) */
  staticContent?: ReactNode;
}

const ScrollExpandHero = ({
  innerImageSrc,
  bgImageSrc,
  innerImageAlt = '',
  bgImageAlt = '',
  slideLeftContent,
  slideRightContent,
  staticContent,
}: ScrollExpandHeroProps) => {
  const progressRef = useRef(0);
  const expandedRef = useRef(false);
  const touchStartYRef = useRef(0);
  const pullDownDistanceRef = useRef(0);
  const touchVelocityRef = useRef(0);
  const lastTouchYRef = useRef(0);
  const lastTouchTimeRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const revertAnimRef = useRef<number | null>(null);
  const autoCompleteRef = useRef<number | null>(null);
  const isMobileRef = useRef(false);

  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const bgFadeRef = useRef<HTMLDivElement>(null);
  const innerTintRef = useRef<HTMLDivElement>(null);
  const slideLeftRef = useRef<HTMLDivElement>(null);
  const slideRightRef = useRef<HTMLDivElement>(null);

  const applyProgress = useCallback((progress: number) => {
    const container = mediaContainerRef.current;
    const hint = hintRef.current;
    const bgFade = bgFadeRef.current;
    const slideLeft = slideLeftRef.current;
    const slideRight = slideRightRef.current;
    const innerTint = innerTintRef.current;
    if (!container) return;

    // Expand inner image
    const widthPercent = 30 + progress * 70;
    const heightPercent = 55 + progress * 45;
    const borderRadius = (1 - progress) * 16;

    container.style.width = `${widthPercent}%`;
    container.style.height = `${heightPercent}%`;
    container.style.borderRadius = `${borderRadius}px`;

    // Fade background to white
    if (bgFade) {
      bgFade.style.opacity = `${progress}`;
    }

    // Slide text out
    if (slideLeft) {
      const translateX = progress * -120;
      slideLeft.style.transform = `translateX(${translateX}%)`;
      slideLeft.style.opacity = `${Math.max(0, 1 - progress * 2.5)}`;
    }
    if (slideRight) {
      const translateX = progress * 120;
      slideRight.style.transform = `translateX(${translateX}%)`;
      slideRight.style.opacity = `${Math.max(0, 1 - progress * 2.5)}`;
    }

    // Hint fade
    if (hint) {
      hint.style.opacity = progress > 0.15 ? '0' : '1';
    }

    // Fade out inner image tint as scroll progresses
    if (innerTint) {
      innerTint.style.opacity = `${Math.max(0, 1 - progress * 2.5)}`;
    }
  }, []);

  const easeProgress = useCallback((linear: number) => {
    if (!isMobileRef.current) return linear;
    return Math.pow(linear, 0.65);
  }, []);

  const updateProgress = useCallback((newProgress: number) => {
    progressRef.current = newProgress;
    const visual = easeProgress(newProgress);

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      applyProgress(visual);
    });

    if (newProgress >= 1 && !expandedRef.current) {
      expandedRef.current = true;
    } else if (newProgress < 1 && expandedRef.current) {
      expandedRef.current = false;
    }
  }, [applyProgress, easeProgress]);

  const smoothRevert = useCallback(() => {
    if (revertAnimRef.current) cancelAnimationFrame(revertAnimRef.current);
    if (autoCompleteRef.current) cancelAnimationFrame(autoCompleteRef.current);
    const animate = () => {
      const current = progressRef.current;
      if (current <= 0.01) {
        updateProgress(0);
        return;
      }
      updateProgress(current * 0.9);
      revertAnimRef.current = requestAnimationFrame(animate);
    };
    revertAnimRef.current = requestAnimationFrame(animate);
  }, [updateProgress]);

  useEffect(() => {
    isMobileRef.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    const handleWheel = (e: WheelEvent) => {
      if (expandedRef.current && e.deltaY < 0 && window.scrollY <= 5) {
        expandedRef.current = false;
        updateProgress(0.99);
        e.preventDefault();
      } else if (!expandedRef.current) {
        e.preventDefault();
        const scrollDelta = e.deltaY * 0.0012;
        const newProgress = Math.min(Math.max(progressRef.current + scrollDelta, 0), 1);
        updateProgress(newProgress);
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      const y = e.touches[0].clientY;
      touchStartYRef.current = y;
      pullDownDistanceRef.current = 0;
      lastTouchYRef.current = y;
      lastTouchTimeRef.current = Date.now();
      touchVelocityRef.current = 0;
      if (revertAnimRef.current) cancelAnimationFrame(revertAnimRef.current);
      if (autoCompleteRef.current) cancelAnimationFrame(autoCompleteRef.current);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartYRef.current) return;
      const touchY = e.touches[0].clientY;
      const deltaY = touchStartYRef.current - touchY;

      const now = Date.now();
      const dt = now - lastTouchTimeRef.current;
      if (dt > 0) {
        touchVelocityRef.current = (lastTouchYRef.current - touchY) / dt;
      }
      lastTouchYRef.current = touchY;
      lastTouchTimeRef.current = now;

      // Once expanded, allow natural scrolling. Only reset on an intentional pull-down at page top.
      if (expandedRef.current) {
        const atTop = window.scrollY <= 5;

        if (atTop && deltaY < 0) {
          pullDownDistanceRef.current += Math.abs(deltaY);
          const intentionalPull = pullDownDistanceRef.current > 110 || deltaY < -55;

          if (intentionalPull) {
            expandedRef.current = false;
            smoothRevert();
            e.preventDefault();
            touchStartYRef.current = touchY;
          }
          return;
        }

        pullDownDistanceRef.current = 0;
        return;
      }

      // Animation in progress
      const scrollFactor = deltaY < 0 ? 0.006 : 0.005;
      const scrollDelta = deltaY * scrollFactor;
      const newProgress = Math.min(Math.max(progressRef.current + scrollDelta, 0), 1);

      // Hand off to natural page scroll a bit before the hard end so momentum survives
      if (newProgress >= 0.92) {
        updateProgress(1);
        touchStartYRef.current = 0;
        return;
      }

      e.preventDefault();
      updateProgress(newProgress);
      touchStartYRef.current = touchY;
    };

    const autoComplete = (scrollPast: number) => {
      if (autoCompleteRef.current) cancelAnimationFrame(autoCompleteRef.current);
      const speed = 0.18;
      const animate = () => {
        const current = progressRef.current;
        if (current >= 0.999) {
          updateProgress(1);
          window.scrollBy({ top: scrollPast, behavior: 'smooth' });
          return;
        }
        updateProgress(current + (1 - current) * speed);
        autoCompleteRef.current = requestAnimationFrame(animate);
      };
      autoCompleteRef.current = requestAnimationFrame(animate);
    };

    const handleTouchEnd = () => {
      const progress = progressRef.current;
      const velocity = Math.max(0, touchVelocityRef.current);

      // Auto-complete only after meaningful progress to avoid fighting the user
      if (!expandedRef.current && progress > 0.15) {
        const scrollPast = Math.min(200, Math.max(100, velocity * 200));
        autoComplete(scrollPast);
      }

      touchStartYRef.current = 0;
    };

    let lastScrollY = 0;
    let scrollTicking = false;
    const handleScroll = () => {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const scrollingUp = currentScrollY < lastScrollY;
        lastScrollY = currentScrollY;

        if (!expandedRef.current) {
          window.scrollTo(0, 0);
        } else if (isMobileRef.current && currentScrollY <= 5 && scrollingUp) {
          expandedRef.current = false;
          smoothRevert();
        }
        scrollTicking = false;
      });
    };

    const handleHeroSkip = () => {
      updateProgress(1);
      expandedRef.current = true;
    };

    window.addEventListener('hero-skip', handleHeroSkip);
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('hero-skip', handleHeroSkip);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (revertAnimRef.current) cancelAnimationFrame(revertAnimRef.current);
      if (autoCompleteRef.current) cancelAnimationFrame(autoCompleteRef.current);
    };
  }, [updateProgress, smoothRevert]);

  return (
    <div className="relative">
      <section className="relative h-screen overflow-hidden">
        <div className="sticky top-0 flex items-center justify-center h-screen">
          {/* Background image */}
          <div ref={bgRef} className="absolute inset-0 z-0 overflow-hidden">
            <img
              src={bgImageSrc}
              alt={bgImageAlt}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Subtle blue-gray tint for text readability */}
            <div className="absolute inset-0 bg-[hsl(220,25%,15%)]/55" />
            {/* White fade overlay */}
            <div
              ref={bgFadeRef}
              className="absolute inset-0 bg-background"
              style={{ opacity: 0 }}
            />
          </div>

          {/* Text content - rendered over background, slides away on scroll */}
          <div className="absolute inset-x-0 top-[80px] bottom-0 z-20 flex items-center justify-center pointer-events-none md:top-0">
            <div className="container max-w-4xl text-center">
              {/* Static content (language toggle) */}
              {staticContent && (
                <div className="pointer-events-auto mb-4">
                  {staticContent}
                </div>
              )}

              {/* Slide LEFT content (h1, reseller) */}
              <div
                ref={slideLeftRef}
                className="will-change-transform pointer-events-auto"
                style={{ transform: 'translateX(0%)', opacity: 1 }}
              >
                {slideLeftContent}
              </div>

              {/* Slide RIGHT content (description, CTAs) */}
              <div
                ref={slideRightRef}
                className="will-change-transform pointer-events-auto"
                style={{ transform: 'translateX(0%)', opacity: 1 }}
              >
                {slideRightContent}
              </div>
            </div>
          </div>

          {/* Expanding inner image */}
          <div className="relative z-10 flex items-center justify-center w-full h-full">
            <div
              ref={mediaContainerRef}
              className="relative overflow-hidden will-change-[width,height,border-radius]"
              style={{
                width: '30%',
                height: '55%',
                borderRadius: '16px',
              }}
            >
              <img
                src={innerImageSrc}
                alt={innerImageAlt}
                fetchPriority="high"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Blue-gray tint that fades out on scroll */}
              <div
                ref={innerTintRef}
                className="absolute inset-0 bg-[hsl(220,25%,15%)]/55"
              />
            </div>
          </div>

          {/* Scroll hint */}
          <div
            ref={hintRef}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden md:flex flex-col items-center gap-2 transition-opacity duration-300"
            style={{ opacity: 1 }}
          >
            <p className="text-white/70 text-sm font-medium tracking-wide uppercase">
              Scroll to explore
            </p>
            <div className="w-5 h-8 border-2 border-white/40 rounded-full flex items-start justify-center p-1">
              <div
                className="w-1.5 h-1.5 bg-white/70 rounded-full animate-[scrollBounce_1.5s_ease-in-out_infinite]"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ScrollExpandHero;
